import React, { useState } from 'react';
import { getCategoryyahooByValue } from '../../utils/categories';
import { FolderOpen } from 'lucide-react';
import { ShippingMethodModal } from './ShippingMethodModal';
import { MemoInput } from './MemoInput';
import { MemoPreview } from './MemoPreview';
import { useTemplateStore } from '../../stores/templateStore';
import { CategorySearchModal } from './CategorySearchModal';
import type { ProductTemplate } from '../../utils/templates/productTemplates';
import { useDescriptionTemplateStore } from '../../stores/descriptionTemplateStore';
import type { GroupSettings } from './BulkImageEditModal';

export interface GroupSettingsProps {
  groupIndex: number;
  settings: GroupSettings;
  selectedTemplate?: ProductTemplate | null;
  onChange: (groupIndex: number, updates: GroupSettings) => void;
}

export function GroupSettings({ 
  groupIndex, 
  settings,
  selectedTemplate,
  onChange 
}: GroupSettingsProps) {
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showCategorySearchModal, setShowCategorySearchModal] = useState(false);
  const [title, setTitle] = useState('');
  const { templates } = useTemplateStore();
  const { templates: descriptionTemplates } = useDescriptionTemplateStore();

  const {
    categoryYahoo,
      startPrice,
      condition,
      memo,
      shippingMethods,
      customShippingMethods,
      shippingResponsibility,
      shippingLater,
      shippingCOD,
      nekoEazySize,
      nekoEazyWeight,
      yuPackSize,
      yuPackWeight,
      descriptionTemplateId,
      productTemplateId,
      autoExtension,
      autoExtensionRate,
      autoExtensionCount,
  } = settings;

  // Check if values have changed from template defaults
  const getDefaultValue = (field: string) => {
    if (!selectedTemplate) {
      // Default values when no template is selected
      switch (field) {
        case 'categoryYahoo': return '';
        case 'startPrice': return 0;
        case 'condition': return '';
        case 'memo': return '';
        case 'shippingMethods': return [];
        case 'customShippingMethods': return [];
        case 'shippingResponsibility': return 'buyer';
        case 'shippingLater': return false;
        case 'shippingCOD': return false;
        case 'nekoEazySize': return '';
        case 'nekoEazyWeight': return '';
        case 'yuPackSize': return '';
        case 'yuPackWeight': return '';
        case 'descriptionTemplateId': return '';
        case 'autoExtension': return false;
        case 'autoExtensionRate': return 5;
        case 'autoExtensionCount': return 1;
        default: return '';
      }
    }
    
    // Get value from selected template
    switch (field) {
      case 'categoryYahoo': return selectedTemplate.fields.categoryYahoo || '';
      case 'startPrice': return selectedTemplate.fields.startPrice || 0;
      case 'condition': return selectedTemplate.fields.condition || '';
      case 'memo': return ''; // memo is not part of template
      case 'shippingMethods': 
        return [
          ...(selectedTemplate.fields.nekoposu ? ['nekoposu'] : []),
          ...(selectedTemplate.fields.nekoCompact ? ['nekoCompact'] : []),
          ...(selectedTemplate.fields.nekoEazy ? ['nekoEazy'] : []),
          ...(selectedTemplate.fields.yuPacketMini ? ['yuPacketMini'] : []),
          ...(selectedTemplate.fields.yuPacket ? ['yuPacket'] : []),
          ...(selectedTemplate.fields.yuPacketPlus ? ['yuPacketPlus'] : []),
          ...(selectedTemplate.fields.yuPack ? ['yuPack'] : [])
        ];
      case 'customShippingMethods': return selectedTemplate.fields.customShippingMethods || [];
      case 'shippingResponsibility': return selectedTemplate.fields.shippingResponsibility || 'buyer';
      case 'shippingLater': return selectedTemplate.fields.shippingLater || false;
      case 'shippingCOD': return selectedTemplate.fields.shippingCOD || false;
      case 'nekoEazySize': return selectedTemplate.fields.nekoEazySize || '';
      case 'nekoEazyWeight': return selectedTemplate.fields.nekoEazyWeight || '';
      case 'yuPackSize': return selectedTemplate.fields.yuPackSize || '';
      case 'yuPackWeight': return selectedTemplate.fields.yuPackWeight || '';
      case 'descriptionTemplateId': return selectedTemplate.fields.descriptionTemplateId || '';
      case 'autoExtension': return selectedTemplate.fields.autoExtension || false;
      case 'autoExtensionRate': return selectedTemplate.fields.autoExtensionRate || 5;
      case 'autoExtensionCount': return selectedTemplate.fields.autoExtensionCount || 1;
      default: return '';
    }
  };

  // Check if current values differ from template defaults
  const compare = (field: keyof GroupSettings) => {
    const current = settings[field];
    const def = getDefaultValue(field as string);
    if (Array.isArray(current) && Array.isArray(def)) {
      return JSON.stringify(current) !== JSON.stringify(def);
    }
    return current !== def;
  };

  const isCategoryChanged = compare('categoryYahoo');
  const isStartPriceChanged = compare('startPrice');
  const isConditionChanged = compare('condition');
  const isMemoChanged = (settings.memo || '') !== getDefaultValue('memo');
  const isShippingMethodsChanged = compare('shippingMethods');
  const isDescriptionTemplateChanged = compare('descriptionTemplateId');
  const isAutoExtensionChanged = compare('autoExtension');
  const isAutoExtensionRateChanged = compare('autoExtensionRate');
  const isAutoExtensionCountChanged = compare('autoExtensionCount');
  const isShippingOptionsChanged =
    compare('shippingResponsibility') ||
    compare('shippingLater') ||
    compare('shippingCOD') ||
    compare('nekoEazySize') ||
    compare('nekoEazyWeight') ||
    compare('yuPackSize') ||
    compare('yuPackWeight') ||
    compare('customShippingMethods');

  const handleTemplateSelect = (templateId: string) => {
    if (!templateId) return;

    onChange(groupIndex, {
      productTemplateId: templateId,
    })
  }

  const handleMemoChange = (newMemo: string) => {
    onChange(groupIndex, { memo: newMemo });
  };

  const handleCategorySelect = (selectedCategoryYahoo: string) => {
    onChange(groupIndex, { categoryYahoo: selectedCategoryYahoo });
    setShowCategorySearchModal(false);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  // Get current category display name
  const currentCategory = getCategoryyahooByValue(categoryYahoo ?? '');
  const categoryDisplayName = currentCategory?.label || 'カテゴリを選択';
  
  // Get truncated category name for button display
  const truncatedCategoryName = categoryDisplayName.length > 20 
    ? `${categoryDisplayName.substring(0, 20)}...` 
    : categoryDisplayName;

  return (
    <div className="mt-4 space-y-4">
      {/* Upper row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Start Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">開始価格</label>
          <input
            type="number"
            value={startPrice || ''}
            onChange={(e) => onChange(groupIndex, { startPrice: Number(e.target.value) })}
            min="0"
            placeholder="開始価格を入力"
            className={`w-full h-[30px] rounded-md border-gray-300 text-sm ${
              isStartPriceChanged ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
            }`}
          />
        </div>

        {/* Condition */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">商品の状態</label>
          <select
            value={condition}
            onChange={(e) => onChange(groupIndex, { condition: e.target.value })}
            className={`w-full h-[30px] rounded-md border-gray-300 text-sm ${
              isConditionChanged ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
            }`}
          >
            <option value="">選択してください</option>
            <option value="new">未使用</option>
            <option value="like-new">未使用に近い</option>
            <option value="good">目立った傷や汚れなし</option>
            <option value="fair">やや傷や汚れあり</option>
            <option value="poor">傷や汚れあり</option>
            <option value="bad">全体的に状態が悪い</option>
          </select>
        </div>

        {/* Category Button */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">カテゴリ</label>
          <button
            onClick={() => setShowCategorySearchModal(true)}
            className={`w-full h-[30px] flex items-center justify-between px-3 rounded-md border text-sm hover:opacity-80 transition-colors ${
              isCategoryChanged 
                ? 'bg-blue-50 border-blue-300 text-blue-700' 
                : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-gray-500" />
              <span className="truncate">
                {isCategoryChanged ? `カテゴリ: ${truncatedCategoryName}` : 'カテゴリを変更'}
              </span>
            </div>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Lower row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Memo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">メモ</label>
          <input
            type="text"
            value={memo}
            onChange={(e) => handleMemoChange(e.target.value)}
            placeholder="商品のメモを入力"
            className={`w-full h-[30px] rounded-md border-gray-300 text-sm ${
              isMemoChanged ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
            }`}
          />
        </div>

        {/* Template */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">テンプレート</label>
          <select
            value={productTemplateId || ''}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            className="w-full h-[30px] rounded-md border-gray-300 bg-gray-50 text-sm"
          >
            <option value="">選択してください</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        {/* Shipping Methods */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">配送方法</label>
          <button
            onClick={() => setShowShippingModal(true)}
            className={`w-full h-[30px] text-center px-4 rounded-md border text-sm transition-colors ${
              isShippingOptionsChanged
                ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {isShippingOptionsChanged ? '配送設定済み' : '配送方法を変更'}
          </button>
        </div>
      </div>

      {/* Description Template Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Description Template */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">説明テンプレート</label>
          <select
            value={descriptionTemplateId}
            onChange={(e) => onChange(groupIndex, { descriptionTemplateId: e.target.value })}
            className={`w-full h-[30px] rounded-md border-gray-300 text-sm ${
              isDescriptionTemplateChanged ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
            }`}
          >
            <option value="">テンプレートを選択</option>
            {descriptionTemplates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        {/* Auto Extension */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">自動再出品</label>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoExtension}
              onChange={(e) => onChange(groupIndex, { autoExtension: e.target.checked })}
              className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                isAutoExtensionChanged ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
              }`}
            />
            <span className="text-sm text-gray-600">有効</span>
          </div>
        </div>

        {/* Auto Extension Rate */}
        {autoExtension && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">値下げ率</label>
            <select
              value={autoExtensionRate}
              onChange={(e) => onChange(groupIndex, { autoExtensionRate: Number(e.target.value) })}
              className={`w-full h-[30px] rounded-md border-gray-300 text-sm ${
                isAutoExtensionRateChanged ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
              }`}
            >
              <option value="0">値下げしない</option>
              <option value="5">5%</option>
              <option value="10">10%</option>
              <option value="15">15%</option>
              <option value="20">20%</option>
              <option value="25">25%</option>
              <option value="30">30%</option>
            </select>
          </div>
        )}
      </div>

      {/* Auto Extension Count (only show if auto extension is enabled) */}
      {autoExtension && (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">再出品回数</label>
            <select
              value={autoExtensionCount}
              onChange={(e) => onChange(groupIndex, { autoExtensionCount: Number(e.target.value) })}
              className={`w-full h-[30px] rounded-md border-gray-300 text-sm ${
                isAutoExtensionCountChanged ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
              }`}
            >
              {[1, 2, 3].map(count => (
                <option key={count} value={count}>{count}回</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showShippingModal && (
        <ShippingMethodModal
          selectedMethods={shippingMethods ?? []}
          customMethods={customShippingMethods ?? []}
          shippingResponsibility={shippingResponsibility}
          shippingLater={shippingLater}
          shippingCOD={shippingCOD}
          nekoEazySize={nekoEazySize}
          nekoEazyWeight={nekoEazyWeight}
          yuPackSize={yuPackSize}
          yuPackWeight={yuPackWeight}
          onSelect={(methods,
            customMethods,
             argShippingResponsibility,
             argShippingLater,
             argShippingCOD,
             argNekoEazySize,
             argNekoEazyWeight,
             argYuPackSize,
             argYuPackWeight,

           ) => {
           onChange(groupIndex, {
             shippingMethods: methods,
             customShippingMethods: customMethods,
             shippingResponsibility: argShippingResponsibility,
             shippingLater: argShippingLater,
             shippingCOD: argShippingCOD,
             nekoEazySize: argNekoEazySize,
             nekoEazyWeight: argNekoEazyWeight,
             yuPackSize: argYuPackSize,
             yuPackWeight: argYuPackWeight,
           });
          }}
          onClose={() => setShowShippingModal(false)}
        />
      )}

      {showCategorySearchModal && (
        <CategorySearchModal
          initialValue={categoryYahoo}
          onSelect={handleCategorySelect}
          onClose={() => setShowCategorySearchModal(false)}
        />
      )}

      {memo && (
        <div className="space-y-2">
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            placeholder="タイトルを入力してプレビュー（*を含めると置換されます）"
            className="w-full rounded-md border-gray-300 bg-gray-50 text-sm"
          />
          <MemoPreview title={title} memo={memo} />
        </div>
      )}
    </div>
  );
}